import { useLocation, useParams } from "wouter";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { useAttributes, useCreateBind, useDataTypes, useRuleBind, useTestRuleAI } from "@/hooks/useLocalStorage";
import { Type, Rule, Code } from "../../shared/schema";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { AlertDialog } from "@/widgets/AlertDailog";
import { useQueryClient } from "@tanstack/react-query";
import { ExpressionBuilderModal } from "../ExpressionRow";


import RuleTypeSelector from "./RuleTypeSelector";
import RuleExpressionSection from "./RuleExpressionSection";
import PricingTable from "./PricingTable";


import { PricingRow, RuleFormData } from "./builderSchema"
import { buildPricingString, buildFinalRule, buildPricingStringFormatted } from "./RuleBuilderFunctions";
import RuleGenerate from "./NestedRule/RuleGenerate";
import { useBreadcrumbs } from "../BreadcrumbContext";

export default function EdiTableRuleBuilder() {
    const { ruleId } = useParams();
    const [location, setLocation] = useLocation();
    const queryClient = useQueryClient();

    const { pushBreadcrumb } = useBreadcrumbs();

    useEffect(() => {
        pushBreadcrumb({
            label: "RuleBuilder",
        });
    }, []);

    const methods = useForm<RuleFormData>({
        defaultValues: {
            ruleType: { value: "-1", label: "" },
            ruleExpression: "",
            ruleExpressionJson: "",
            rows: [{ id: 1, isEditing: true, isNew: true, condition: "If" }],
            NestedRule: {
                groups: [
                    {
                        id: 'group-1',
                        type: 'group',
                        conditions: [
                            {
                                id: 'cond-1',
                                dataType: '',
                                attribute: '',
                                operator: '==',
                                valueType: 'operand',
                                dataType2: '',
                                attribute2: ''
                            }
                        ],
                        logicalOperators: []
                    }
                ],
                groupOperators: []
            },
            NestedRuleString: "",
            rawNestedRuleString: "",
            DatatypeAttributeMap: {},
            pricingRows: [{
                id: 1,
                isEditing: true,
                dataType: "",
                attribute: "",
                operator: "=",
                dataType2: "",
                attribute2: "",
                expressionType: "operand",
                expressionValue: "",
                ExpressionJson: "",
                isNew: true
            }],
            finalRule: "",
            isValidated: false
        }
    });

    const { watch, setValue } = methods;
    const ruleExpression = watch("ruleExpression");
    const ruleExpressionJson = watch("ruleExpressionJson");
    const rows = watch("rows");
    const NestedRule = watch("NestedRule");
    const pricingRows = watch("pricingRows");
    const ruleType = watch("ruleType");
    const finalRule = watch("finalRule");
    const isValidated = watch("isValidated");
    const NestedRuleStringDa = watch("NestedRuleString");
    const rawNestedRuleStringDa = watch("rawNestedRuleString");
    const DatatypeAttributeMap = watch("DatatypeAttributeMap");

    const [dataType, setDataType] = useState<Type[]>([]);
    const [attribute, setAttribute] = useState<Code[]>([]);
    const [attrCode, setAttrCode] = useState("");
    const [showExpressionBuilder, setShowExpressionBuilder] = useState(false);
    const [showPricingAdjustment, setShowPricingAdjustment] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);
    const [openExpression, setOpenExpression] = useState(false);
    const [expressionData, setExpressionData] = useState<any>(null);
    const [ruleObjValue, setRuleObjValue] = useState<Rule>();
    const [pricingFormatted, setPricingFormatted] = useState<string>("");

    const { data: existingDtValues } = useDataTypes(1, undefined, undefined);
    const { data: existingAttribute } = useAttributes(attrCode);
    const { data: ruleBindVal } = useRuleBind(ruleId ?? "");
    const updateRuleMutation = useCreateBind();
    const testRuleData = useTestRuleAI()

    let ruleObj = queryClient.getQueryData(["selectedRule"]) as Rule | undefined;

    useEffect(() => {
        if (!ruleObj) {
            const saved = localStorage.getItem("selectedRule");
            if (saved) {
                ruleObj = JSON.parse(saved);
                queryClient.setQueryData(["selectedRule"], ruleObj);
            }
        }
        if (ruleObj) {
            setValue("ruleType", {
                value: `${ruleObj?.Type}`,
                label: getRuleTypeLabel(String(ruleObj.Type))
            });
            setRuleObjValue(ruleObj)
        }
    }, []);

    const ruleTypeOptions = [
        { value: "0", label: "Eligibility Rules" },
        { value: "1", label: "Pricing Rules" },
        { value: "2", label: "Credit Rating Rules" },
        { value: "4", label: "Workflow Entry Conditions" },
        { value: "5", label: "Compliance Rules" },
        { value: "6", label: "Form Rules" },
    ];

    const getRuleTypeLabel = (typeValue: string) => {
        const option = ruleTypeOptions.find((opt) => opt.value === typeValue);
        return option ? option.label : "";
    };

    useEffect(() => {
        if (existingDtValues && existingDtValues.total > 0) {
            setDataType(existingDtValues.items);
        }
    }, [existingDtValues]);

    useEffect(() => {
        if (existingAttribute && existingAttribute.length > 0) {
            setAttribute(existingAttribute);
        } else {
            setAttribute([]);
        }
    }, [existingAttribute]);

    useEffect(() => {
        if (ruleBindVal) {
            if (ruleBindVal.RuleExpression !== "") {
                setValue("ruleExpression", String(ruleBindVal.RuleExpression));
            }

            if (ruleBindVal.RuleExpressionJson !== "") {
                setValue("ruleExpressionJson", String(ruleBindVal.RuleExpressionJson));
            }

            if (ruleBindVal.RuleType !== undefined) {
                setValue("ruleType", {
                    value: String(ruleBindVal.RuleType),
                    label: getRuleTypeLabel(String(ruleBindVal.RuleType))
                });
            }

            if (ruleBindVal.RawRuleJson !== undefined) {
                setValue("NestedRule", ruleBindVal.RawRuleJson);
            }

            if (ruleBindVal.pricing && ruleBindVal.pricing.length > 0) {
                const mappedPricingRows: PricingRow[] = ruleBindVal.pricing.map((pricing: any, index: number) => ({
                    id: index + 1,
                    isEditing: false,
                    dataType: pricing.Data_Type || "",
                    attribute: pricing.Attribute_Name || "",
                    operator: pricing.Operator?.trim() || "=",
                    dataType2: pricing.Exp_DataType || "",
                    attribute2: pricing.Exp_Attribute || "",
                    expressionType: pricing.Exp_Value ? "value" : "operand",
                    expressionValue: pricing.Exp_Value || "",
                    ExpressionJson: pricing.ExpressionJson || "",
                    isNew: false
                }));
                setValue("pricingRows", mappedPricingRows);
                setShowPricingAdjustment(true);
            }
        }
    }, [ruleBindVal]);


    useEffect(() => {
        const completedPricingRows = pricingRows.filter(row => !row.isEditing);

        const pricingString = buildPricingString(completedPricingRows);
        const pricingStringFormatted = buildPricingStringFormatted(completedPricingRows);
        const finalRuleString = buildFinalRule(NestedRuleStringDa, pricingString);
        setPricingFormatted(pricingStringFormatted)
        if (!finalRuleString) {
            setValue("finalRule", `${!!pricingString ? `\n${pricingString}\n}EndIf` : pricingString}`);
        } else {
            setValue("finalRule", `If${finalRuleString}`);
        }
    }, [NestedRuleStringDa, pricingRows]);

    const validateRule = () => {
        const payload = {
            prompt: `If(${NestedRuleStringDa}){}EndIf`,
            test_data: DatatypeAttributeMap
        };

        testRuleData.mutateAsync(payload);
    }
    // console.log("rawNestedRuleStringDa", rawNestedRuleStringDa)
    // console.log("NestedRuleStringDa", NestedRuleStringDa)
    // console.log("DatatypeAttributeMap", DatatypeAttributeMap)

    const onSubmit = async () => {
        try {
            const completedPricingRows = pricingRows.filter(row => !row.isEditing);
            const ruleString =
                ruleType.value === "6"
                    ? `{\n${buildPricingString(completedPricingRows)}\n}EndIf`
                    : `${finalRule}`
            const pricingString = buildPricingString(completedPricingRows);

            const pricing = completedPricingRows.map((pricingRow) => ({
                Data_Type: pricingRow.dataType || "",
                Attribute_Name: pricingRow.attribute || "",
                Operator: pricingRow.operator || "",
                Value_Type: pricingRow.expressionType || "value",
                Expression: pricingString,
                ExpressionJson: pricingRow.expressionType === "value" ? pricingRow.ExpressionJson || "" : "",
                Exp_DataType: pricingRow.expressionType === "operand" ? pricingRow.dataType2 || "" : "",
                Exp_Attribute: pricingRow.expressionType === "operand" ? pricingRow.attribute2 || "" : "",
                Exp_Value: pricingRow.expressionType === "value" ? pricingRow.expressionValue || "" : "",
                StartGroup: "",
                EndGroup: "",
                RootValue: "",
                RootDataType: "",
                RootAttribute: ""
            }));

            const data = {
                script: {
                    Body: ruleString,
                    Type: parseInt(ruleType.value),
                    RuleExpression: ruleExpression,
                    RuleExpressionJson: ruleExpressionJson,
                    RawRuleJson: NestedRule,
                    AiRuleExpression: `${pricingFormatted ? `If${rawNestedRuleStringDa}\n{\n    ${pricingFormatted}\n}EndIf` : `If${rawNestedRuleStringDa}{}EndIf`}`
                },
                pricing: pricing,
                replace_pricing: true
            };

            const payload = {
                id: ruleId ?? "",
                body: data
            };

            await updateRuleMutation.mutateAsync(payload);
        } catch (error) {
            console.error("Error creating rule:", error);
        }
    };

    const navigateback = () => {
        setAlertOpen(false)
        if (isValidated) {
            setLocation("/");
        }
    }

    useEffect(() => {
        if (updateRuleMutation.isSuccess === true) {
            setReqStatus(updateRuleMutation.isSuccess);
            setAlertMessage(`Rule Build Successfully!`);
            setAlertOpen(true);
            updateRuleMutation.reset();
        } else if (updateRuleMutation.isError) {
            const raw = updateRuleMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(updateRuleMutation.isSuccess);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            updateRuleMutation.reset();
        }
    }, [updateRuleMutation]);

    useEffect(() => {
        if (testRuleData.isSuccess === true) {
            setReqStatus(testRuleData.isSuccess);
            setAlertMessage(`${testRuleData.data.success ? "Rule Validation Successfully!" : testRuleData.data.detail}`);
            if (testRuleData.data.success) {
                setValue("isValidated", true);
            } else {
                setValue("isValidated", false);
            }
            setAlertOpen(true);
            testRuleData.reset();
        } else if (testRuleData.isError) {
            const raw = testRuleData.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(testRuleData.isSuccess);
            setAlertMessage(parsedMsg);
            setValue("isValidated", false);
            setAlertOpen(true);
            testRuleData.reset();
        }
    }, [testRuleData]);

    const allowedTypes = [2, 3, 4, 5, 6, 8,];
    const allAllowedTypes = [9, 10, 11];

    return (
        <FormProvider {...methods}>
            <div className="p-6 space-y-6">
                <h1 className="text-xl font-semibold">Edit Rule Builder</h1>
                <div>
                    <div className="flex items-center justify-between">
                        <div className="flex">
                            <h2 className="text-l font-semibold mr-2">Rule:</h2>
                            <h2>{ruleId}{" "}({ruleObjValue?.Name})</h2>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setLocation("/")}
                            className="flex items-center gap-1"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </Button>
                    </div>
                    {ruleObjValue?.Approved === 1 &&
                        <div >
                            <p className=" text-sm text-red-600">UnApprove the Rule, for Edit Access</p>
                        </div>
                    }
                </div>

                <RuleExpressionSection
                    showExpressionBuilder={showExpressionBuilder}
                    setShowExpressionBuilder={setShowExpressionBuilder}
                    dataType={dataType}
                />

                <RuleTypeSelector
                    ruleTypeOptions={ruleTypeOptions}
                />

                {ruleType.label !== "Form Rules" &&
                    <RuleGenerate
                        dataType={dataType}
                        // attribute={attribute}
                        attribute={attribute?.filter(
                            item =>
                                item.type !== undefined &&
                                !allAllowedTypes.includes(Number(item.type))
                        )}
                        setAttrCode={setAttrCode}
                        version={false}
                    />
                }

                {(ruleType.label === "Pricing Rules" || ruleType.label === "Form Rules") && (
                    <div className="pt-6">
                        {!showPricingAdjustment ? (
                            <button
                                onClick={() => setShowPricingAdjustment(true)}
                                className="text-blue-600 underline text-sm hover:text-blue-800"
                            >
                                Do you want to make Rule Adjustment?
                            </button>
                        ) : (
                            <PricingTable
                                dataType={dataType}
                                attribute={attribute?.filter(item =>
                                    item.type !== undefined &&
                                    allowedTypes.includes(Number(item.type))
                                )}
                                setAttrCode={setAttrCode}
                                onClose={() => setShowPricingAdjustment(false)}
                                openExpression={openExpression}
                                setOpenExpression={setOpenExpression}
                                setExpressionData={setExpressionData}
                            />
                        )}
                    </div>
                )}

                <div className="border bg-white border-gray-200 rounded-lg p-2">
                    <label className="font-medium text-gray-700">Rules in Final Form</label>
                    <Textarea
                        value={finalRule}
                        className="w-full md:w-full sm:w-3/4 bg-gray-100 text-gray-800 border border-black rounded-md disabled:text-gray-800 disabled:opacity-100"
                        disabled
                        rows={5}
                    />
                </div>

                <div className="flex justify-center items-center gap-4 pt-4">
                    <Button
                        variant="outline"
                        onClick={validateRule}
                    >

                        {testRuleData.isPending ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Validating...
                            </>
                        ) : (
                            "Validate Script"
                        )}
                    </Button>

                    <Button
                        disabled={!isValidated || ruleObjValue?.Approved === 1}
                        onClick={onSubmit}
                    >
                        {updateRuleMutation.isPending ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Saving...
                            </>
                        ) : (
                            "Save Rule"
                        )}
                    </Button>
                </div>

                <AlertDialog
                    open={alertOpen}
                    onOpenChange={(alertMessage === "Rule Build Successfully!") ? navigateback : () => setAlertOpen(false)}
                    message={alertMessage}
                    status={reqStatus}
                />

                <ExpressionBuilderModal
                    open={openExpression}
                    onClose={() => {
                        setOpenExpression(false)
                        setExpressionData(null);
                    }}
                    initialExpression={expressionData?.expressionValue || ""}
                    initialExpressionJson={expressionData?.ExpressionJson ? JSON.parse(expressionData.ExpressionJson) : null}
                    onSave={({ finalExpression, expressionJson }) => {
                        if (expressionData) {
                            const currentRows = methods.getValues("pricingRows");
                            const updatedRows = currentRows.map((row) =>
                                row.id === expressionData.id
                                    ? {
                                        ...row,
                                        expressionValue: finalExpression,
                                        ExpressionJson: JSON.stringify(expressionJson),
                                    }
                                    : row
                            );
                            setValue("pricingRows", updatedRows);
                        }
                        setOpenExpression(false);
                        setExpressionData(null);
                    }}
                />
            </div>
        </FormProvider>
    );
}